import { type CardNetworkEnum } from "@finway-group/shared/lib/models/card/cardNetwork.enum"
import { useQuery } from "@tanstack/react-query"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { CardService } from "~Shared/services/card.service"
import type { CreditCardDocument } from "~Shared/services/card.types"
import { CacheEnum } from "./consts"

export const cardQueryKey = (id: string) => [CacheEnum.CARDS, id] as const

export const cardIssuanceNetworkQueryKey = [CacheEnum.CARDS, "issuance-network"] as const

/** Bridge key when the only stable input is an expense id (`GET /expenses/:id/card/`). */
export const cardByExpenseQueryKey = (expenseId: string) => [CacheEnum.CARDS, "expense", expenseId] as const

export const invalidateCardQuery = (id: string) => queryClient.invalidateQueries({ queryKey: cardQueryKey(id) })

export const invalidateCardByExpenseQuery = (expenseId: string) => queryClient.invalidateQueries({ queryKey: cardByExpenseQueryKey(expenseId) })

export type UseCardQuerySource = string | undefined | { cardId?: string; expenseId?: string }

const resolveCardQuerySource = (source: UseCardQuerySource): { cardId?: string; expenseId?: string } => {
    if (source === undefined) {
        return {}
    }
    if (typeof source === "string") {
        return { cardId: source }
    }
    return { cardId: source.cardId, expenseId: source.expenseId }
}

/**
 * Network for newly issued cards (from company wallet bank). Cached for the session — bank mapping is stable.
 */
export const useCardIssuanceNetwork = (enabled = true) =>
    useQuery<{ network: CardNetworkEnum }>({
        queryKey: cardIssuanceNetworkQueryKey,
        queryFn: ({ signal }) => CardService.fetchIssuanceNetwork(signal),
        enabled,
        staleTime: Number.POSITIVE_INFINITY,
    })

/**
 * Load a card by mongo id (`GET /cards/:id`) or, with `{ expenseId }`, by expense (`GET /expenses/:id/card/`).
 * The expense path writes the same object into `cardQueryKey(card._id)` so all `useCard(cardId)` readers stay aligned.
 */
export const useCard = (source: UseCardQuerySource) => {
    const { cardId, expenseId } = resolveCardQuerySource(source)
    const enabled = !!cardId || !!expenseId

    return useQuery<CreditCardDocument>({
        queryKey: cardId ? cardQueryKey(cardId) : expenseId ? cardByExpenseQueryKey(expenseId) : [CacheEnum.CARDS, ""],
        queryFn: async ({ signal }) => {
            if (cardId) {
                return CardService.fetchCardById(cardId, signal)
            }
            if (expenseId) {
                const card = await CardService.fetchCardByExpenseId(expenseId, signal)
                const resolvedId = String(card._id ?? "")
                if (resolvedId) {
                    queryClient.setQueryData(cardQueryKey(resolvedId), card)
                }
                return card
            }
            throw new Error("useCard: pass a card id string or { cardId } / { expenseId }")
        },
        enabled,
    })
}
